import { z } from 'zod';
import { jsonObject, jsonValue } from './primitives';

/**
 * CanonicalStep (§6.4 `agent_run_steps`, §8.2). The canonical run transcript.
 * Two writers allocate step numbers from one atomic counter (§7.2): the
 * ToolGateway writes `tool_call` steps; the episode runner writes the rest.
 * `EpisodeResult.steps` therefore contains NO tool executions — only model turns
 * and control/system steps that reference gateway-written tool step numbers.
 */
export const stepKind = z.enum(['model_turn', 'tool_call', 'approval', 'wake', 'system']);
export type StepKind = z.infer<typeof stepKind>;

export const toolStepStatus = z.enum(['executing', 'ok', 'failed', 'denied', 'ambiguous']);
export type ToolStepStatus = z.infer<typeof toolStepStatus>;

export const stepCost = z
  .object({
    costUsd: z.number().nonnegative().optional(),
    tokensIn: z.number().int().nonnegative().optional(),
    tokensOut: z.number().int().nonnegative().optional(),
  })
  .optional();

/**
 * A step as authored by a writer, before the platform assigns `stepNo` from the
 * atomic allocator (§7.2). The platform stamps `stepNo`, `runId`, `workspaceId`.
 */
export const canonicalStep = z.object({
  kind: stepKind,
  payload: jsonObject,
  tool: z.string().nullable().optional(),
  toolStatus: toolStepStatus.nullable().optional(),
  cost: stepCost,
});
export type CanonicalStep = z.infer<typeof canonicalStep>;

/** A persisted step with its allocated identity (as returned to the web transcript). */
export const persistedStep = canonicalStep.extend({
  runId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  stepNo: z.number().int().positive(),
  idempotencyKey: z.string().nullable().optional(),
  payloadOverflow: jsonValue.optional(), // spilled to artifacts when > limits (§9.6)
});
export type PersistedStep = z.infer<typeof persistedStep>;
